import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import { useEffect, useState } from "react";

import { analyticsEvent } from "@up/analytics";
import { logError, logWarn } from "@up/data";

import type { TScholarship } from "@up/data";
import type { HTTPError } from "ky";
import type { Dispatch, ReactNode, SetStateAction } from "react";

type Status = "idle" | "ready" | "loading" | "error";

type RenderProps = {
  data?: TScholarship;
  setData: Dispatch<SetStateAction<TScholarship | undefined>>;
  fetchStatus: Status;
  saveStatus: Status;
  save: (updatedData: TScholarship) => Promise<void>;
};

const BASE_URL = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"];

const STORAGE_KEY_PREFIX = "scholarship_submitted_";

function getPersistedScholarship(partnerID: string): TScholarship | undefined {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${partnerID}`);
    if (!raw) return undefined;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || !("firstName" in parsed)) {
      sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${partnerID}`);
      return undefined;
    }
    return parsed as TScholarship;
  } catch (error: unknown) {
    logWarn({ action: "SCHOLARSHIP_PERSIST_READ", error, context: { partnerID } });
    return undefined;
  }
}

function persistScholarship(partnerID: string, scholarship: TScholarship) {
  try {
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${partnerID}`, JSON.stringify(scholarship));
  } catch (error: unknown) {
    logWarn({ action: "SCHOLARSHIP_PERSIST_WRITE", error, context: { partnerID } });
  }
}

export function LoadScholarshipData({
  children,
  contactId,
  email,
  partnerID,
}: {
  children: (data: RenderProps) => ReactNode;
  contactId: string;
  email: string;
  partnerID: string;
}) {
  const auth = useAuthStore();
  const persisted = getPersistedScholarship(partnerID);
  const [data, setData] = useState<TScholarship | undefined>(persisted);
  const [fetchStatus, setFetchStatus] = useState<Status>(persisted ? "ready" : "idle");
  const [saveStatus, setSaveStatus] = useState<Status>("idle");

  const save = async function (updatedData: Partial<TScholarship>) {
    const URL = `${BASE_URL}/v2/scholarships/${partnerID}`;
    setSaveStatus("loading");
    persistScholarship(partnerID, { ...updatedData, createdAt: new Date().toISOString() } as TScholarship);

    try {
      const { data: response } = await auth.client
        .post(URL, {
          json: {
            ...updatedData,
            owner: email,
            ownerContactId: contactId,
          },
        })
        .json<{ data: TScholarship }>();
      setData(response);
      persistScholarship(partnerID, response);

      if (response.createdAt) {
        analyticsEvent({
          event: "scholarshipSubmit",
        });
      }

      setSaveStatus("ready");
    } catch (err: unknown) {
      try { sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${partnerID}`); } catch {}
      setSaveStatus("error");
      logError({ action: "SCHOLARSHIP_SAVE", error: err, context: { partnerID } });
    }
  };

  useEffect(
    function checkForReturningStudent() {
      const getExistingData = async () => {
        const URL = `${BASE_URL}/v2/scholarships/${partnerID}`;
        setFetchStatus("loading");

        try {
          const { data: res } = await auth.client.get(URL).json<{ data: TScholarship }>();
          if (res) {
            setData(res);
          } else {
            throw new Error("User data was empty");
          }
          setFetchStatus("ready");
        } catch (_err: unknown) {
          const error = _err as HTTPError;
          // 404 means no existing data
          if (error.response.status === 404) {
            setFetchStatus("ready");
          } else {
            setFetchStatus("error");
            logError({ action: "SCHOLARSHIP_FETCH", error: _err, context: { contactId, partnerID } });
          }
        }
      };

      if (auth.currentUser && fetchStatus === "idle") {
        getExistingData();
      }
    },
    [auth, data, contactId, partnerID, fetchStatus],
  );

  const value: RenderProps = {
    data,
    setData,
    save,
    saveStatus,
    fetchStatus,
  };

  return <>{children(value)}</>;
}
